"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import {
    AlertCircle,
    Building2,
    Check,
    CheckCircle2,
    ClipboardList,
    Clock,
    Loader2,
    MapPin,
    Minus,
    Plus,
    ShoppingBag,
    Trash2,
    User,
} from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useInquiryCart } from "@/contexts/InquiryCartContext";
import { InquiryService } from "@/server/cart/inquiry.service";
import CustomItemModal from "@/components/cart/CustomItemModal";
import { PRICE_UNIT, formatPriceFa, hasPrice, toPrice } from "@/lib/inquiry-price";
import type {
    InquiryCustomer,
    InquiryItem,
    InquirySubmitPayload,
    InquirySubmitResponse,
} from "@/types/inquiry";

type Step = "cart" | "customer" | "review" | "success";

const DRAFT_KEY = "ittelecom_inquiry_draft";

const EMPTY_CUSTOMER: InquiryCustomer = {
    invoiceType: "unofficial",
    name: "",
    company: "",
    mobile: "",
    email: "",
    province: "",
    city: "",
    address: "",
    zipcode: "",
    economicCode: "",
    nationalId: "",
    notes: "",
};

type UserInfo = {
    name?: string;
    phone?: string;
    mobile?: string;
    email?: string;
};

type UserLike = {
    info?: UserInfo | string | null;
    firstName?: string;
    lastName?: string;
    cellphone?: string;
    phone?: string;
    email?: string;
};

type ApiErrorData = {
    message?: string;
    errors?: Record<string, string[] | string>;
};


const URGENCY_OPTIONS = ["عادی", "فوری (تا ۷ روز)", "خیلی فوری (۲۴ ساعت)"];

const STEPS: [Exclude<Step, "success">, string][] = [
    ["cart", "سبد خرید"],
    ["customer", "اطلاعات مشتری و پروژه"],
    ["review", "بررسی نهایی"],
];

/* ---------------- helpers ---------------- */

const toEn = (s: string) =>
    s
        .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)))
        .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)));

const normalizeMobile = (s: string) => toEn(s).replace(/[\s-]/g, "");
const MOBILE_RE = /^(\+98|0098|98|0)?9\d{9}$/;

function readUserDefaults(user: unknown) {
    const u = (user ?? {}) as UserLike;

    let info: UserInfo = {};
    if (typeof u.info === "string") {
        try {
            info = (JSON.parse(u.info) as UserInfo | null) ?? {};
        } catch {
            info = {};
        }
    } else if (u.info) {
        info = u.info;
    }

    return {
        name: info.name || [u.firstName, u.lastName].filter(Boolean).join(" ") || "",
        mobile: u.cellphone || u.phone || info.phone || info.mobile || "",
        email: u.email || info.email || "",
    };
}

function extractError(err: unknown): { status?: number; message: string } {
    const e = err as { response?: { status?: number; data?: ApiErrorData } };
    const status = e?.response?.status;
    const data = e?.response?.data;
    if (status === 422 && data?.errors) {
        const first = Object.values(data.errors)[0];
        const msg = Array.isArray(first) ? first[0] : first;
        if (typeof msg === "string") return { status, message: msg };
    }
    if (typeof data?.message === "string") return { status, message: data.message };
    return { status, message: "ثبت درخواست با خطا مواجه شد. لطفاً دوباره تلاش کنید." };
}

/* ---------------- small components ---------------- */

const pill = "whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs";

function PriceCell({ it }: { it: InquiryItem }) {
    if (it.type === "site" && hasPrice(it.price)) {
        const unit = toPrice(it.price);
        return (
            <div className="shrink-0 text-end text-xs">
                <div className="text-gray-500">
                    قیمت واحد: {formatPriceFa(unit)} {PRICE_UNIT}
                </div>
                <div className="text-sm font-bold text-gray-900">
                    {formatPriceFa(unit * it.qty)} {PRICE_UNIT}
                </div>
            </div>
        );
    }
    return <span className={`${pill} shrink-0 border-gray-300 bg-gray-50 text-gray-600`}>استعلام</span>;
}

function TypeTag({ type }: { type: InquiryItem["type"] }) {
    return type === "site" ? (
        <span className={`${pill} border-gray-200 text-gray-500`}>محصول سایت</span>
    ) : (
        <span className={`${pill} border-amber-400 bg-amber-50 text-amber-700`}>درخواست سفارشی</span>
    );
}

function SectionCard({
                         icon,
                         title,
                         children,
                     }: {
    icon: React.ReactNode;
    title: string;
    children: React.ReactNode;
}) {
    return (
        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <h3 className="mb-4 flex items-center gap-2 text-sm font-bold text-gray-900">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#252360]/10 text-[#252360]">
                    {icon}
                </span>
                {title}
            </h3>
            {children}
        </div>
    );
}

/* ---------------- main ---------------- */

export default function InquiryCartClient() {
    const { items, hydrated, totalLines, totalUnits, setQty, removeItem, addCustomItem, clear } =
        useInquiryCart();
    const { user, isAuthenticated, isLoading: authLoading, openAuthModal } = useAuth();

    const [step, setStep] = useState<Step>("cart");
    const [customer, setCustomer] = useState<InquiryCustomer>(EMPTY_CUSTOMER);
    const [urgency, setUrgency] = useState(URGENCY_OPTIONS[0]);
    const [draftLoaded, setDraftLoaded] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [confirmed, setConfirmed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [result, setResult] = useState<InquirySubmitResponse | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    // بازیابی پیش‌نویس فرم
    useEffect(() => {
        try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (raw) setCustomer({ ...EMPTY_CUSTOMER, ...JSON.parse(raw) });
        } catch {
            /* ignore */
        }
        setDraftLoaded(true);
    }, []);

    // ذخیره‌ی پیش‌نویس فرم
    useEffect(() => {
        if (!draftLoaded) return;
        try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify(customer));
        } catch {
            /* ignore */
        }
    }, [customer, draftLoaded]);

    // پرکردن خودکار از اطلاعات کاربر واردشده
    useEffect(() => {
        if (!draftLoaded || !user) return;
        const d = readUserDefaults(user);
        setCustomer((c) => ({
            ...c,
            name: c.name || d.name,
            mobile: c.mobile || d.mobile,
            email: c.email || d.email,
        }));
    }, [draftLoaded, user]);

    // اسکرول به بالا با تغییر مرحله
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [step]);

    // اگر سبد خالی شد، از مراحل بعدی برگرد
    useEffect(() => {
        if (hydrated && items.length === 0 && (step === "customer" || step === "review")) {
            setStep("cart");
        }
    }, [hydrated, items.length, step]);

    const setField =
        (k: keyof InquiryCustomer) =>
            (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                setCustomer((c) => ({ ...c, [k]: e.target.value }));
                setErrors((er) => ({ ...er, [k]: "" }));
            };

    const goReview = (e: React.FormEvent) => {
        e.preventDefault();
        const er: Record<string, string> = {};
        if (!customer.name.trim()) er.name = "نام و نام خانوادگی الزامی است.";
        if (!MOBILE_RE.test(normalizeMobile(customer.mobile)))
            er.mobile = "شماره موبایل معتبر نیست (مثال: 09121234567).";
        if (customer.email.trim() && !/^\S+@\S+\.\S+$/.test(customer.email.trim()))
            er.email = "ایمیل معتبر نیست.";
        // فاکتور رسمی: اطلاعات حقوقی الزامی است
        if (customer.invoiceType === "official" && !toEn(customer.nationalId).trim())
            er.nationalId = "برای فاکتور رسمی، شناسه ملی / شماره ثبت شرکت الزامی است.";
        setErrors(er);
        if (Object.keys(er).length) return;
        setConfirmed(false);
        setSubmitError("");
        setStep("review");
    };

    const submit = async () => {
        if (!isAuthenticated) {
            setSubmitError("برای ثبت درخواست ابتدا وارد حساب کاربری شوید.");
            openAuthModal("login");
            return;
        }
        setSubmitting(true);
        setSubmitError("");

        // فوریت به ابتدای توضیحات پروژه اضافه می‌شود تا نیازی به تغییر تایپ‌ها نباشد
        const notes = [urgency !== URGENCY_OPTIONS[0] ? `فوریت: ${urgency}` : "", customer.notes.trim()]
            .filter(Boolean)
            .join("\n");

        const payload: InquirySubmitPayload = {
            customer: {
                name: customer.name.trim(),
                company: customer.company.trim() || undefined,
                mobile: normalizeMobile(customer.mobile),
                email: customer.email.trim() || undefined,
                province: customer.province.trim() || undefined,
                city: customer.city.trim() || undefined,
                zipcode: customer.zipcode.trim() || undefined,
                address: customer.address.trim() || undefined,
                // اطلاعات حقوقی فقط برای فاکتور رسمی ارسال می‌شود
                economic_code:
                    customer.invoiceType === "official"
                        ? toEn(customer.economicCode).trim() || undefined
                        : undefined,
                national_id:
                    customer.invoiceType === "official"
                        ? toEn(customer.nationalId).trim() || undefined
                        : undefined,
            },
            invoice_type: customer.invoiceType,
            project_notes: notes || undefined,
            items: items.map((i) => ({
                type: i.type,
                product_id: i.type === "site" ? i.productId : undefined,
                name: i.name,
                part: i.part || undefined,
                brand: i.brand || undefined,
                qty: i.qty,
                description: i.description || undefined,
                link: i.link || undefined,
            })),
        };

        try {
            const res = await InquiryService.submit(payload);
            setResult(res);
            clear();
            try {
                localStorage.removeItem(DRAFT_KEY);
            } catch {
                /* ignore */
            }
            setStep("success");
        } catch (err) {
            const { status, message } = extractError(err);
            if (status === 401) {
                setSubmitError("نشست شما منقضی شده است. لطفاً دوباره وارد شوید.");
                openAuthModal("login");
            } else {
                setSubmitError(message);
            }
        } finally {
            setSubmitting(false);
        }
    };

    /* ---------- pieces ---------- */

    const pricedTotal = items.reduce(
        (sum, i) => (i.type === "site" && hasPrice(i.price) ? sum + toPrice(i.price) * i.qty : sum),
        0
    );
    const inquiryCount = items.filter((i) => !(i.type === "site" && hasPrice(i.price))).length;

    const totalsBlock = (
        <div className="space-y-2 text-sm">
            {pricedTotal > 0 && (
                <div className="flex justify-between">
                    <span className="text-gray-500">جمع اقلام دارای قیمت</span>
                    <span className="font-bold">
                        {formatPriceFa(pricedTotal)} {PRICE_UNIT}
                    </span>
                </div>
            )}
            {inquiryCount > 0 && (
                <div className="flex justify-between">
                    <span className="text-gray-500">اقلام نیازمند استعلام</span>
                    <span>{formatPriceFa(inquiryCount)} قلم</span>
                </div>
            )}
        </div>
    );

    const stepper = (
        <ol className="mb-8 flex items-center gap-2 text-xs sm:text-sm">
            {STEPS.map(([key, label], idx) => {
                const current = STEPS.findIndex(([k]) => k === step);
                const active = idx === current;
                const done = idx < current;
                return (
                    <li key={key} className="flex flex-1 items-center gap-2 last:flex-none">
                        <span
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold transition ${
                                active
                                    ? "bg-[#252360] text-white shadow-md shadow-[#252360]/30"
                                    : done
                                        ? "bg-green-600 text-white"
                                        : "bg-gray-200 text-gray-500"
                            }`}
                        >
                            {done ? <Check className="h-4 w-4" /> : idx + 1}
                        </span>
                        <span
                            className={`${active ? "font-bold text-gray-900" : "text-gray-500"} ${
                                active ? "" : "hidden sm:inline"
                            }`}
                        >
                            {label}
                        </span>
                        {idx < STEPS.length - 1 && (
                            <span className={`h-0.5 flex-1 rounded ${done ? "bg-green-600" : "bg-gray-200"}`} />
                        )}
                    </li>
                );
            })}
        </ol>
    );

    const itemRow = (it: InquiryItem, editable: boolean) => (
        <div
            key={it.key}
            className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-100 bg-white p-3.5 shadow-sm transition hover:shadow-md"
        >
            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gray-50 text-[9px] text-gray-400">
                {it.image ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={it.image} alt={it.name} className="h-full w-full object-contain" loading="lazy" />
                ) : (
                    <ShoppingBag className="h-6 w-6 text-gray-300" />
                )}
            </div>

            <div className="min-w-[150px] flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                    {it.type === "site" && it.slug ? (
                        <Link
                            href={`/${process.env.NEXT_PUBLIC_SHOP_PRODUCT_SINGLE || "shop"}/${it.slug}`}
                            className="text-sm font-semibold hover:text-blue-700"
                        >
                            {it.name}
                        </Link>
                    ) : (
                        <span className="text-sm font-semibold">{it.name}</span>
                    )}
                    <TypeTag type={it.type} />
                </div>
                <div className="text-xs text-gray-500">
                    پارت نامبر: <span dir="ltr">{it.part || "—"}</span>
                    {it.type === "custom" && <>، برند: {it.brand || "—"}</>}
                </div>
                {it.type === "custom" && it.description && (
                    <div className="mt-1 text-xs text-gray-500">{it.description}</div>
                )}
            </div>

            <PriceCell it={it} />

            {editable ? (
                <>
                    <div className="flex shrink-0 items-center overflow-hidden rounded-xl border border-gray-200">
                        <button
                            type="button"
                            onClick={() => setQty(it.key, it.qty + 1)}
                            className="flex h-9 w-8 items-center justify-center hover:bg-gray-100"
                            aria-label="افزایش"
                        >
                            <Plus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-8 text-center text-sm font-semibold">{it.qty}</span>
                        <button
                            type="button"
                            onClick={() => setQty(it.key, it.qty - 1)}
                            className="flex h-9 w-8 items-center justify-center hover:bg-gray-100"
                            aria-label="کاهش"
                        >
                            <Minus className="h-3.5 w-3.5" />
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={() => removeItem(it.key)}
                        className="flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                        حذف
                    </button>
                </>
            ) : (
                <span className="shrink-0 text-sm">{it.qty} عدد</span>
            )}
        </div>
    );

    /* ---------- render ---------- */

    if (!hydrated && step !== "success") {
        return (
            <div className="flex justify-center py-20 text-gray-400">
                <Loader2 className="h-6 w-6 animate-spin" />
            </div>
        );
    }

    /* ===== موفقیت ===== */
    if (step === "success" && result) {
        return (
            <section className="mx-auto max-w-lg rounded-2xl bg-white p-8 text-center shadow-sm">
                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-green-50">
                    <CheckCircle2 className="h-12 w-12 text-green-600" />
                </div>
                <h1 className="mb-2 text-2xl font-bold">درخواست استعلام شما با موفقیت ثبت شد.</h1>
                <p className="mb-6 text-sm text-gray-600">
                    درخواست شما برای واحد فروش ارسال شد. کارشناسان فروش پس از بررسی اقلام و قیمت‌ها،
                    پیش‌فاکتور را در پنل کاربری شما قرار خواهند داد.
                </p>

                <div className="mb-6 inline-block rounded-xl border border-gray-200 px-8 py-3">
                    <div className="mb-1 text-xs text-gray-500">شماره درخواست</div>
                    <div className="text-lg font-bold" dir="ltr">
                        {result.number}
                    </div>
                    <span className={`${pill} mt-2 inline-block border-amber-400 bg-amber-50 text-amber-700`}>
                        در انتظار بررسی فروش
                    </span>
                </div>

                <ol className="mb-8 space-y-3 text-start text-sm text-gray-600">
                    {[
                        "دریافت درخواست شما",
                        "بررسی قیمت و موجودی توسط واحد فروش",
                        "صدور پیش‌فاکتور در پنل کاربری",
                    ].map((t, i) => (
                        <li key={t} className="flex items-center gap-3">
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#252360]/10 text-xs font-bold text-[#252360] dark:text-white dark:bg-gray-300">
                                {i + 1}
                            </span>
                            {t}
                        </li>
                    ))}
                </ol>

                <div className="flex flex-wrap justify-center gap-3">
                    <Link href="/shop" className={buttonVariants({ variant: "default" })}>
                        بازگشت به فروشگاه
                    </Link>
                    <Link
                        href={process.env.NEXT_PUBLIC_URL_DASHBOARD || "/"}
                        className={buttonVariants({ variant: "default" })}
                    >
                        ورود به پنل کاربری
                    </Link>
                </div>
            </section>
        );
    }

    /* ===== سبد خالی ===== */
    if (items.length === 0) {
        return (
            <Card className="hover:shadow-sm">
                <div className="flex flex-col items-center justify-center px-4 py-20 text-center">
                    <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full border border-gray-100 bg-gray-50">
                        <ShoppingBag className="h-10 w-10 text-gray-500" />
                    </div>
                    <h2 className="mb-1 text-base font-bold text-gray-900">سبد استعلام شما خالی است.</h2>
                    <p className="mb-5 text-sm text-gray-500">محصولات مورد نیاز خود را از فروشگاه اضافه کنید.</p>
                    <Link href="/shop" className={buttonVariants({ variant: "default" })}>
                        مشاهده محصولات
                    </Link>
                </div>
            </Card>
        );
    }

    /* ===== مرحله ۱: سبد ===== */
    if (step === "cart") {
        return (
            <section>
                {stepper}
                <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
                    <div className="space-y-3">

                        <h1 className="mb-1 text-2xl font-bold">سبد خرید محصولات</h1>
                        <p className="mb-6 text-sm text-gray-600">
                            محصولات و تجهیزات موردنیاز خود را بررسی کنید. پس از تأیید، درخواست شما برای واحد فروش ارسال خواهد شد.
                        </p>

                        {items.map((it) => itemRow(it, true))}

                        <button
                            type="button"
                            onClick={() => setModalOpen(true)}
                            className="w-full rounded-2xl cursor-pointer border-2 border-dashed border-gray-200 bg-white py-4 text-sm text-gray-700 transition hover:border-[#252360]/40 hover:bg-gray-50"
                        >
                            + افزودن محصول یا تجهیزات موردنیاز
                        </button>
                    </div>

                    <aside className="h-fit space-y-3 rounded-2xl border border-gray-100 bg-white p-5 text-sm shadow-sm lg:sticky lg:top-24">
                        <h2 className="text-sm font-bold">خلاصه درخواست</h2>
                        <div className="flex justify-between">
                            <span className="text-gray-500">تعداد کل اقلام</span>
                            <span>{totalLines}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">تعداد کل واحدها</span>
                            <span>{totalUnits}</span>
                        </div>
                        <div className="border-t border-gray-100 pt-3">{totalsBlock}</div>
                        <div className="rounded-xl bg-[#252360]/5 p-3 text-xs text-gray-600">
                            قیمت نهایی پس از بررسی توسط واحد فروش اعلام می‌شود.
                        </div>
                        <Button className={buttonVariants({ variant: "success", className: "w-full" })} onClick={() => setStep("customer")}>
                            ادامه و ثبت درخواست استعلام
                        </Button>
                        <Link href="/shop" className={buttonVariants({ variant: "default", className: "w-full" })}>
                            ادامه انتخاب محصولات
                        </Link>
                    </aside>
                </div>

                <CustomItemModal open={modalOpen} onClose={() => setModalOpen(false)} onAdd={addCustomItem} />
            </section>
        );
    }

    /* ===== مرحله ۲: اطلاعات مشتری و پروژه ===== */
    if (step === "customer") {
        const field = (
            k: keyof InquiryCustomer,
            label: string,
            opts: { required?: boolean; ltr?: boolean; type?: string } = {}
        ) => (
            <div>
                <label className="mb-1 block text-xs text-gray-600">
                    {label}
                    {opts.required && " *"}
                </label>
                <Input
                    value={customer[k]}
                    onChange={setField(k)}
                    dir={opts.ltr ? "ltr" : undefined}
                    type={opts.type}
                    aria-invalid={!!errors[k]}
                    maxLength={k === "address" ? 1000 : 200}
                />
                {errors[k] && (
                    <p role="alert" className="mt-1 text-xs text-red-600">
                        {errors[k]}
                    </p>
                )}
            </div>
        );

        return (
            <>
                {stepper}
                <section className="m-auto max-w-2xl">
                    <h1 className="mb-1 text-2xl font-bold">اطلاعات مشتری و پروژه</h1>
                    <p className="mb-6 text-sm text-gray-600">این اطلاعات برای تماس و صدور پیش‌فاکتور استفاده می‌شود.</p>

                    <form onSubmit={goReview} className="space-y-4" noValidate>
                        <SectionCard icon={<User className="h-4 w-4 dark:text-white" />} title="اطلاعات تماس">
                            <div className="grid gap-3 md:grid-cols-2">
                                {field("name", "نام و نام خانوادگی", { required: true })}
                                {field("company", "نام شرکت / سازمان")}
                                {field("mobile", "شماره موبایل", { required: true, ltr: true })}
                                {field("email", "ایمیل", { ltr: true, type: "email" })}
                            </div>
                        </SectionCard>

                        <SectionCard icon={<MapPin className="h-4 w-4 dark:text-white" />} title="آدرس">
                            <div className="grid gap-3 md:grid-cols-2">
                                {field("province", "استان")}
                                {field("city", "شهر")}
                                <div className="md:col-span-2">
                                    <label className="mb-1 block text-xs text-gray-600">آدرس</label>
                                    <Textarea
                                        rows={2}
                                        value={customer.address}
                                        onChange={setField("address")}
                                        maxLength={1000}
                                    />
                                </div>
                                {field("zipcode", "کد پستی")}
                            </div>
                        </SectionCard>

                        <SectionCard icon={<Building2 className="h-4 w-4 dark:text-white" />} title="صدور فاکتور">
                            <div className="mb-4 flex flex-wrap gap-3" role="radiogroup" aria-label="نوع صدور">
                                {(
                                    [
                                        ["official", "فاکتور رسمی"],
                                        ["unofficial", "پیش فاکتور غیررسمی"],
                                    ] as const
                                ).map(([value, label]) => (
                                    <label
                                        key={value}
                                        className={`flex cursor-pointer items-center gap-2 rounded-xl border px-4 py-2 text-sm transition ${
                                            customer.invoiceType === value
                                                ? "border-[#252360] bg-[#252360]/5 font-semibold"
                                                : "border-gray-200 hover:bg-gray-50"
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="invoiceType"
                                            value={value}
                                            checked={customer.invoiceType === value}
                                            onChange={() => {
                                                setCustomer((c) => ({ ...c, invoiceType: value }));
                                                setErrors((er) => ({ ...er, nationalId: "", economicCode: "" }));
                                            }}
                                            className="h-4 w-4 accent-[#252360]"
                                        />
                                        {label}
                                    </label>
                                ))}
                            </div>

                            {customer.invoiceType === "official" && (
                                <div className="grid gap-3 md:grid-cols-2">
                                    {field("nationalId", "شناسه ملی / شماره ثبت شرکت", { required: true, ltr: true })}
                                    {field("economicCode", "کد اقتصادی شرکت", { ltr: true })}
                                </div>
                            )}
                        </SectionCard>

                        <SectionCard icon={<Clock className="h-4 w-4 dark:text-white" />} title="فوریت و توضیحات پروژه">
                            <div className="mb-4 flex flex-wrap gap-2">
                                {URGENCY_OPTIONS.map((u) => (
                                    <button
                                        key={u}
                                        type="button"
                                        onClick={() => setUrgency(u)}
                                        className={`rounded-full border px-4 py-1.5 text-sm transition ${
                                            urgency === u
                                                ? "border-[#252360] bg-[#252360] text-white"
                                                : "border-gray-200 text-gray-700 hover:bg-gray-50"
                                        }`}
                                    >
                                        {u}
                                    </button>
                                ))}
                            </div>
                            <Textarea
                                rows={3}
                                value={customer.notes}
                                onChange={setField("notes")}
                                maxLength={3000}
                                placeholder="مثلاً: این تجهیزات برای توسعه شبکه کارخانه استفاده می‌شود و تحویل حداکثر تا ۲۰ روز موردنیاز است."
                            />
                        </SectionCard>

                        <div className="flex gap-3">
                            <Button type="submit" className={buttonVariants({ variant: "success", className: "flex-1" })}>
                                ادامه به بررسی نهایی
                            </Button>
                            <Button type="button" variant="default" onClick={() => setStep("cart")}>
                                بازگشت به سبد خرید
                            </Button>
                        </div>
                    </form>
                </section>
            </>
        );
    }

    /* ===== مرحله ۳: بررسی نهایی ===== */
    const summaryRows: [string, string][] = [
        ["نام و نام خانوادگی", customer.name],
        ["شرکت", customer.company],
        ["موبایل", normalizeMobile(customer.mobile)],
        ["ایمیل", customer.email],
        ["استان", customer.province],
        ["شهر", customer.city],
        ["آدرس", customer.address],
        ["کد پستی", customer.zipcode],
        ["نوع صدور", customer.invoiceType === "official" ? "فاکتور رسمی" : "پیش فاکتور غیررسمی"],
        ["کد اقتصادی", customer.invoiceType === "official" ? customer.economicCode : ""],
        ["شناسه ملی / شماره ثبت", customer.invoiceType === "official" ? customer.nationalId : ""],
        ["فوریت", urgency],
        ["توضیحات پروژه", customer.notes],
    ];

    return (
        <>
            {stepper}
            <section className="m-auto max-w-2xl">
                <h1 className="mb-1 text-2xl font-bold">بررسی نهایی درخواست</h1>
                <p className="mb-6 text-sm text-gray-600">اطلاعات زیر را پیش از ارسال بررسی کنید.</p>

                <div className="mb-4 space-y-2 rounded-2xl border border-gray-100 bg-white p-5 text-sm shadow-sm">
                    {summaryRows
                        .filter(([, v]) => v.trim())
                        .map(([label, v]) => (
                            <div key={label} className="flex justify-between gap-3">
                                <span className="shrink-0 text-gray-500">{label}</span>
                                <span className="text-start">{v}</span>
                            </div>
                        ))}
                </div>

                <div className="mb-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-bold">
                        <ClipboardList className="h-4 w-4" />
                        اقلام درخواستی ({totalLines} قلم، {totalUnits} واحد)
                    </h3>
                    <div className="space-y-2">{items.map((it) => itemRow(it, false))}</div>
                    <div className="mt-4 border-t border-gray-100 pt-3">{totalsBlock}</div>
                </div>

                {!authLoading && !isAuthenticated && (
                    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        <span>برای ثبت درخواست باید وارد حساب کاربری شوید. سبد و اطلاعات شما حفظ می‌شود.</span>
                        <Button type="button" size="sm" onClick={() => openAuthModal("login")}>
                            ورود / ثبت‌نام
                        </Button>
                    </div>
                )}

                <label className="mb-4 flex cursor-pointer items-start gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={confirmed}
                        onChange={(e) => setConfirmed(e.target.checked)}
                        className="mt-1"
                    />
                    اطلاعات درخواست را بررسی کرده‌ام و آماده ارسال است.
                </label>

                {submitError && (
                    <p role="alert" className="mb-3 rounded-xl bg-red-50 p-3 text-sm text-red-700">
                        {submitError}
                    </p>
                )}

                <div className="flex gap-3">
                    <Button className={buttonVariants({ variant: "success", className: "flex-1" })} disabled={!confirmed || submitting} onClick={submit}>
                        {submitting && <Loader2 className="animate-spin" />}
                        ثبت و ارسال درخواست استعلام
                    </Button>
                    <Button variant="default" disabled={submitting} onClick={() => setStep("customer")}>
                        ویرایش اطلاعات
                    </Button>
                </div>
            </section>
        </>
    );
}
